import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { initNotificationSound } from '../utils/notificationSound';

// Hooks
import { useChatState } from '../hooks/useChatState';
import { useChatApi } from '../hooks/useChatApi';
import { useWebSocket } from '../hooks/useWebSocket';
import { usePMWindows } from '../hooks/usePMWindows';
import { useMessageHandlers } from '../hooks/useMessageHandlers';
import { useSecureUrl } from '../hooks/useSecureUrl';

// Components
import URLViewerModal from '../components/URLViewerModal';
import PMWindow from '../components/PMWindow';
import RoomSettingsModal from '../components/RoomSettingsModal';
import Footer from '../components/Footer';
import AdScriptLoader from '../components/AdScriptLoader';
import MobileHeader, { MobileTabNav } from '../components/chat/MobileHeader';
import { NotificationBanners } from '../components/chat/NotificationBanners';
import { ConnectionStatus } from '../components/chat/ConnectionStatus';
import { MediaLightbox } from '../components/chat';

// Layout Components
import DesktopChatLayout from './chat/DesktopChatLayout';
import MobileChatLayout from './chat/MobileChatLayout';

const ChatPage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize all state via custom hook
  const state = useChatState(user);
  
  // Initialize API functions
  const api = useChatApi(state, user, navigate);
  
  // Initialize WebSocket
  const { connectWebSocket } = useWebSocket(state, api, user, setUser);
  
  // Initialize PM windows
  const pmWindows = usePMWindows(state, user);
  const { fetchUnreadPmCount } = pmWindows;
  
  // Initialize message handlers
  const messageHandlers = useMessageHandlers(state, user);
  
  // Initialize secure URL handling
  const secureUrl = useSecureUrl();
  
  // Destructure commonly used state
  const {
    rooms, selectedRoom, setSelectedRoom,
    messages, members, setMessages,
    openPMWindows,
    mobileNavTab, setMobileNavTab,
    activeTab, setActiveTab,
    wsConnected, reconnecting,
    visibleNotifications, needsVerification,
    lightboxMedia, setLightboxMedia,
    roomSettingsOpen, setRoomSettingsOpen,
    leaveRoomDialogOpen, setLeaveRoomDialogOpen,
    leaveRoomLoading, setLeaveRoomLoading,
    roomToLeave, setRoomToLeave,
    wsRef
  } = state;
  
  const {
    loadRooms, loadFriends, loadGames, 
    loadGlobalTheme, loadAdminNotifications, 
    selectRoom, handleJoinRoom
  } = api;

  // Initialize app on mount
  useEffect(() => {
    const initializeApp = async () => {
      try {
        await axios.post(`${API}/init`);
        loadGlobalTheme();
        loadRooms().then(roomsData => {
          // Find Global Chat as default room
          const globalChat = roomsData?.find((r) => 
            r.name.toLowerCase() === 'global chat' || 
            r.name.toLowerCase() === 'public chat' ||
            r.type === 'public'
          );
          if (globalChat && !selectedRoom) {
            selectRoom(globalChat);
          }
        });
        loadFriends();
        loadGames();
        fetchUnreadPmCount();
        loadAdminNotifications();
        connectWebSocket();
        initNotificationSound();
      } catch (error) {
        console.error('Init error:', error);
      }
    };
    
    initializeApp();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Handle PM redirect from profile page
  useEffect(() => {
    if (location.state?.openPM && location.state?.pmUserId) {
      const pmUser = {
        id: location.state.pmUserId,
        username: location.state.pmUsername
      };
      pmWindows.openPMWindow(pmUser);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (!detail.friendId) return;
      pmWindows.openPMWindow({
        id: detail.friendId,
        username: detail.friendUsername || 'User',
        callId: detail.callId || null,
        callParticipants: Array.isArray(detail.participants) ? detail.participants : []
      });
      setActiveTab('friends');
    };
    window.addEventListener('open-pm-call', handler);
    return () => window.removeEventListener('open-pm-call', handler);
  }, [pmWindows, setActiveTab]);

  // Handle room redirect from profile page
  useEffect(() => {
    if (location.state?.roomId && rooms.length > 0) {
      const targetRoom = rooms.find(room => room.id === location.state.roomId);
      if (targetRoom) {
        selectRoom(targetRoom);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [rooms, location.state]);

  // Restore chat state when coming back from profile
  useEffect(() => {
    const savedState = sessionStorage.getItem('chatState');
    if (savedState && rooms.length > 0) {
      try {
        const { roomId, mobileNavTab: savedMobileNavTab, activeTab: savedActiveTab } = JSON.parse(savedState);
        if (savedMobileNavTab) setMobileNavTab(savedMobileNavTab);
        if (savedActiveTab) setActiveTab(savedActiveTab);
        if (roomId) {
          const targetRoom = rooms.find(room => room.id === roomId);
          if (targetRoom && (!selectedRoom || selectedRoom.id !== roomId)) {
            selectRoom(targetRoom);
          }
        }
        sessionStorage.removeItem('chatState');
      } catch (error) {
        console.error('Failed to restore chat state:', error);
        sessionStorage.removeItem('chatState');
      }
    }
  }, [rooms]);

  // Auto-scroll when messages change or when room switches.
  useEffect(() => {
    const id = setTimeout(() => state.scrollToBottom(), 30);
    return () => clearTimeout(id);
  }, [messages, selectedRoom?.id, state.scrollToBottom]);

  // Live desync watchdog: lightweight periodic sync for active room while tab is visible.
  useEffect(() => {
    if (!selectedRoom?.id) return undefined;

    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const mergeMessages = (latest) => {
      setMessages((prev) => {
        const map = new Map();
        [...prev, ...(latest || [])].forEach((m) => {
          if (m?.id) map.set(m.id, m);
        });
        return Array.from(map.values()).sort((a, b) =>
          String(a.created_at || '').localeCompare(String(b.created_at || ''))
        );
      });
    };

    const syncNow = async () => {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;

      try {
        const [messagesRes] = await Promise.all([
          axios.get(`${API}/rooms/${selectedRoom.id}/messages?limit=20`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetchUnreadPmCount()
        ]);
        mergeMessages(messagesRes.data || []);
      } catch (error) {
        // Keep silent; this is a best-effort background watchdog.
      }
    };

    const intervalId = setInterval(syncNow, 25000);
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        syncNow();
      }
    };

    document.addEventListener('visibilitychange', visibilityHandler);
    syncNow();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [selectedRoom?.id, setMessages, fetchUnreadPmCount]);

  useEffect(() => {
    if (user?.dark_mode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [user]);

  // Handlers
  const handleLogout = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
    localStorage.removeItem('token');
    sessionStorage.removeItem('ws_connection_id');
    document.documentElement.classList.remove('dark');
    if (wsRef.current) {
      wsRef.current.close();
    }
    setUser(null);
    navigate('/');
  }, [navigate, setUser, wsRef]);

  const navigateToDashboard = useCallback(() => {
    if (user.role === 'super_admin') {
      window.location.href = '/super-admin-dashboard';
    } else if (user.role === 'staff') {
      window.location.href = '/staff-dashboard';
    } else if (user.role === 'premium') {
      window.location.href = '/premium';
    }
  }, [user.role]);

  const confirmLeaveRoom = useCallback(async () => {
    const targetRoom = roomToLeave || selectedRoom;
    if (!targetRoom) return;
    
    setLeaveRoomLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/rooms/${targetRoom.id}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Left room successfully');
      setLeaveRoomDialogOpen(false);
      setRoomToLeave(null);
      
      const roomsResponse = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      state.setRooms(roomsResponse.data);
      
      if (selectedRoom?.id === targetRoom.id) {
        const globalChat = roomsResponse.data.find((r) => 
          r.name.toLowerCase() === 'global chat' || 
          r.name.toLowerCase() === 'public chat'
        );
        if (globalChat) {
          selectRoom(globalChat);
          setActiveTab('rooms');
        } else {
          setSelectedRoom(null);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave room');
    } finally {
      setLeaveRoomLoading(false);
    }
  }, [roomToLeave, selectedRoom, selectRoom, setActiveTab, setSelectedRoom, state.setRooms, setLeaveRoomDialogOpen, setRoomToLeave, setLeaveRoomLoading]);

  // Compute user's role in current room
  const userRoomRole = user.role === 'super_admin' || user.role === 'staff' 
    ? 'owner' 
    : (members.find(m => m.user_id === user.id)?.role || '');

  const isActualRoomOwner = selectedRoom?.owner_id === user.id;
  
  const canLeaveRoom = selectedRoom && 
    !isActualRoomOwner && 
    selectedRoom.name !== 'Global Chat' && 
    selectedRoom.name !== 'public chat';

  // Combine all props for layout components
  const layoutProps = {
    user,
    setUser,
    state,
    api,
    pmWindows,
    messageHandlers,
    secureUrl,
    userRoomRole,
    isActualRoomOwner,
    canLeaveRoom,
    handleLogout,
    navigateToDashboard,
    confirmLeaveRoom
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 overflow-hidden">
      {/* URL Viewer Modal */}
      <URLViewerModal
        isOpen={secureUrl.urlModalOpen}
        onClose={() => secureUrl.setUrlModalOpen(false)}
        url={secureUrl.viewUrl}
      />
      
      {/* Notification Banners */}
      <NotificationBanners
        user={user}
        setUser={setUser}
        needsVerification={needsVerification}
        visibleNotifications={visibleNotifications}
        dismissNotification={state.dismissNotification}
      />
      
      {/* Mobile Header */}
      <MobileHeader
        user={user}
        selectedRoom={selectedRoom}
        mobileNavTab={mobileNavTab}
        activeTab={activeTab}
        handleLogout={handleLogout}
        navigateToDashboard={navigateToDashboard}
      />
      
      {/* Mobile Tab Navigation */}
      <MobileTabNav
        mobileNavTab={mobileNavTab}
        setMobileNavTab={setMobileNavTab}
        setActiveTab={setActiveTab}
        unreadPmCount={state.unreadPmCount}
      />
      
      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Connection Status */}
        <ConnectionStatus wsConnected={wsConnected} reconnecting={reconnecting} />
        
        {/* Mobile Layout */}
        <MobileChatLayout {...layoutProps} />
        
        {/* Desktop Layout */}
        <DesktopChatLayout {...layoutProps} />
      </div>
      
      {/* PM Windows */}
      {openPMWindows.map((pmWindow, index) => (
        <PMWindow
          key={pmWindow.friend.id}
          friend={pmWindow.friend}
          user={user}
          ws={state.ws}
          isMinimized={pmWindow.isMinimized}
          isMaximized={pmWindow.isMaximized}
          onClose={() => pmWindows.closePMWindow(pmWindow.friend.id)}
          onMinimize={() => pmWindows.toggleMinimizePMWindow(pmWindow.friend.id)}
          onMaximize={() => pmWindows.toggleMaximizePMWindow(pmWindow.friend.id)}
          zIndex={50 + index}
          position={index}
          fetchUnreadPmCount={pmWindows.fetchUnreadPmCount}
        />
      ))}
      
      {/* Media Lightbox */}
      {lightboxMedia && (
        <MediaLightbox
          media={lightboxMedia}
          onClose={() => setLightboxMedia(null)}
        />
      )}
      
      {/* Room Settings Modal */}
      {roomSettingsOpen && selectedRoom && (
        <RoomSettingsModal
          open={roomSettingsOpen}
          onClose={() => setRoomSettingsOpen(false)}
          room={selectedRoom}
          userRole={userRoomRole}
          members={members}
          currentUserId={user.id}
          onUpdate={() => {
            api.loadRooms();
            api.selectRoom(selectedRoom);
          }}
        />
      )}
      
      {/* Footer & Ads */}
      <Footer />
      <AdScriptLoader />
    </div>
  );
};

export default ChatPage;
